import React, { useEffect, useState, useRef, useCallback } from 'react'
import api from '../../../api'
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion'
import { useUser } from '../../../authentication/authentication';
import {
    Bot,
    User,
    ArrowLeft,
    ArrowRight,
    Maximize2,
    Minimize2,
    X,
    Send,
    AlertTriangle,
    MessageSquare,
    Loader2,
    Trash2,
    Info,
} from 'lucide-react'
import { arClass } from "../../../utils/useArClass";
import Txt from "../../../components/Text";
import logos from "../../../images/logos";
import textTranslator from "../../../utils/useTextTranslator";
const Text = ({ children, className, page = "AI" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const POSITION_KEY = 'ai_panel_position'
const SIZE_KEY = 'ai_panel_size'
const WIDTH_KEY = 'ai_panel_width'
const HEIGHT_KEY = 'ai_panel_height'

const MIN_WIDTH = 400
const MAX_WIDTH = 900
const DEFAULT_WIDTH = 420
const DEFAULT_HEIGHT_RATIO = 0.85
const MIN_HEIGHT_RATIO = 0.6

function readStored(key, fallback) {
    try {
        const val = localStorage.getItem(key)
        return val || fallback
    } catch {
        return fallback
    }
}

function readStoredNumber(key, fallback) {
    try {
        const val = localStorage.getItem(key)
        const num = parseInt(val, 10)
        return Number.isFinite(num) ? num : fallback
    } catch {
        return fallback
    }
}

const SENSITIVE_TYPE_LABELS = {
    email: "email address",
    phone: "phone number",
    national_id: "national ID (CIN)",
    passport: "passport number",
    credit_card: "credit card number",
    iban: "bank account (IBAN)",
    ssn: "national identifier",
    date_of_birth: "date of birth",
    address: "physical address",
    name: "personal name",
}

// For the Text component - we'll use keys
const SENSITIVE_TYPE_LABELS_KEYS = {
    email: "email_address",
    phone: "phone_number",
    national_id: "national_id",
    passport: "passport_number",
    credit_card: "credit_card",
    iban: "bank_account",
    ssn: "national_identifier",
    date_of_birth: "date_of_birth",
    address: "physical_address",
    name: "personal_name",
}

let msgCounter = 0
const genId = () => `msg_${Date.now()}_${msgCounter++}`

export default function AiSidePanel() {
    const darkMode = useSelector((state) => state.settings.darkMode);
    const user = useUser()
    const firstName = user?.name?.split(' ')[0]
    const [prompt, setPrompt] = useState('')
    const [loading, setLoading] = useState(false)

    const [messages, setMessages] = useState([])

    const [usage, setUsage] = useState(null)
    const [usageLoading, setUsageLoading] = useState(true)
    const [errorMsg, setErrorMsg] = useState(null)
    const [sensitiveTypes, setSensitiveTypes] = useState([])

    const [isOpen, setIsOpen] = useState(false)
    const [position, setPosition] = useState(() => readStored(POSITION_KEY, 'right'))
    const [size, setSize] = useState(() => readStored(SIZE_KEY, 'small'))

    const [width, setWidth] = useState(() => readStoredNumber(WIDTH_KEY, DEFAULT_WIDTH))
    const [height, setHeight] = useState(() => {
        const fallback = typeof window !== 'undefined'
            ? Math.round(window.innerHeight * DEFAULT_HEIGHT_RATIO)
            : 640
        return readStoredNumber(HEIGHT_KEY, fallback)
    })
    const [isResizing, setIsResizing] = useState(false)

    const dragState = useRef(null)
    const scrollEndRef = useRef(null)

    useEffect(() => {
        try { localStorage.setItem(POSITION_KEY, position) } catch {}
    }, [position])

    useEffect(() => {
        try { localStorage.setItem(SIZE_KEY, size) } catch {}
    }, [size])

    useEffect(() => {
        try { localStorage.setItem(WIDTH_KEY, String(width)) } catch {}
    }, [width])

    useEffect(() => {
        try { localStorage.setItem(HEIGHT_KEY, String(height)) } catch {}
    }, [height])

    useEffect(() => {
        const enforceMinHeight = () => {
            const minH = Math.round(window.innerHeight * MIN_HEIGHT_RATIO)
            const maxH = window.innerHeight - 16
            setHeight((prev) => Math.min(Math.max(prev, minH), maxH))
        }
        enforceMinHeight()
        window.addEventListener('resize', enforceMinHeight)
        return () => window.removeEventListener('resize', enforceMinHeight)
    }, [])

    useEffect(() => {
        scrollEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }, [messages, loading])

    useEffect(() => {
        if (!isOpen) return
        const id = requestAnimationFrame(() => {
            scrollEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
        })
        return () => cancelAnimationFrame(id)
    }, [isOpen])

    const fetchUsage = () => {
        setUsageLoading(true)
        api.get('/ai/usage')
            .then((response) => {
                setUsage(response.data?.data || null)
            })
            .catch((error) => {
                //pass
            })
            .finally(() => setUsageLoading(false))
    }

    useEffect(() => {
        fetchUsage()
    }, [])

    const handleSubmit = (e) => {
        e.preventDefault()
        const trimmed = prompt.trim()
        if (!trimmed) return

        setErrorMsg(null)
        setSensitiveTypes([])

        setMessages((prev) => [...prev, { id: genId(), role: 'user', content: trimmed }])
        setPrompt('')
        setLoading(true)

        api.post('/ai/generate', { prompt: trimmed }).then((response) => {
            const data = response.data
            setMessages((prev) => [...prev, {
                id: genId(),
                role: 'assistant',
                content: data.data?.text || data.message,
                model: data.data?.model,
                usage: data.data?.usage,
            }])
            setLoading(false)
            fetchUsage()
        }).catch((error) => {
            console.error('Error fetching AI response:', error)
            setLoading(false)
            setPrompt(trimmed)

            const status = error?.response?.status
            const data = error?.response?.data

            if (status === 400 && data?.data?.detectedTypes) {
                setErrorMsg(data.error)
                setSensitiveTypes(data.data.detectedTypes)
            } else if (status === 429) {
                setErrorMsg(data?.error || "Daily limit reached — try again tomorrow")
                fetchUsage()
            } else if (status === 400) {
                setErrorMsg(data?.error || "Invalid prompt")
            } else {
                setErrorMsg("Something went wrong. Please try again.")
            }
        })
    }

    const handleChange = (e) => {
        setPrompt(e.target.value)
        if (errorMsg) {
            setErrorMsg(null)
            setSensitiveTypes([])
        }
    }

    const clearChat = () => {
        setMessages([])
        setErrorMsg(null)
        setSensitiveTypes([])
    }

    const isLeft = position === 'left'
    const isFull = size === 'full'
    const canSubmit = prompt.trim() && usage?.remainingRequests !== 0

    const handleMouseMoveRef = useRef(() => {})
    const stopResizingRef = useRef(() => {})

    const stopResizing = useCallback(() => {
        dragState.current = null
        setIsResizing(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        window.removeEventListener('mousemove', handleMouseMoveRef.current)
        window.removeEventListener('mouseup', stopResizingRef.current)
    }, [])
    stopResizingRef.current = stopResizing

    const handleMouseMove = useCallback((e) => {
        const drag = dragState.current
        if (!drag) return

        if (drag.axis === 'width') {
            const dir = isLeft ? 1 : -1
            const delta = (e.clientX - drag.startX) * dir
            const next = Math.min(Math.max(drag.startWidth + delta, MIN_WIDTH), Math.min(MAX_WIDTH, window.innerWidth - 40))
            setWidth(next)
        } else if (drag.axis === 'height') {
            const delta = drag.startY - e.clientY
            const minH = Math.round(window.innerHeight * MIN_HEIGHT_RATIO)
            const maxH = window.innerHeight - 16
            const next = Math.min(Math.max(drag.startHeight + delta, minH), maxH)
            setHeight(next)
        }
    }, [isLeft])
    handleMouseMoveRef.current = handleMouseMove

    const beginResize = useCallback((axis) => (e) => {
        e.preventDefault()
        dragState.current = {
            axis,
            startX: e.clientX,
            startY: e.clientY,
            startWidth: width,
            startHeight: height,
        }
        setIsResizing(true)
        document.body.style.cursor = axis === 'width' ? 'ew-resize' : 'ns-resize'
        document.body.style.userSelect = 'none'
        window.addEventListener('mousemove', handleMouseMoveRef.current)
        window.addEventListener('mouseup', stopResizingRef.current)
    }, [width, height])

    useEffect(() => {
        return () => {
            window.removeEventListener('mousemove', handleMouseMoveRef.current)
            window.removeEventListener('mouseup', stopResizingRef.current)
        }
    }, [])

    const sideClass = isLeft ? 'left-0' : 'right-0'

    const iconBtnClass = darkMode
        ? 'text-gray-400 hover:text-white hover:bg-gray-800'
        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'

    const panelStyle = isFull
        ? undefined
        : { width: `${width}px`, height: `${height}px` }

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className={arClass(
                            `fixed inset-0 z-40 ${
                                isFull ? 'bg-black/50 backdrop-blur-sm' : 'bg-black/20 sm:bg-transparent'
                            }`
                        )}
                        onClick={() => setIsOpen(false)}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {!isOpen && (
                    <motion.button
                        key="toggle"
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.7 }}
                        transition={{ duration: 0.18 }}
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setIsOpen(true)}
                        className={arClass(
                            `fixed z-[9999] bottom-6 ${isLeft ? 'left-5' : 'right-5'}
                            w-14 h-14 rounded-full shadow-xl shadow-black/50 flex items-center justify-center
                             text-white ${darkMode ? 'bg-slate-600 hover:bg-slate-700' : 'bg-slate-200 hover:bg-slate-300'} `
                        )}
                        aria-label="Open AI Assistant"
                    >
                        <img
                            src={darkMode ? logos.lightLogo : logos.darkLogo}
                            alt={'Raqib AI Assistant'}
                            className={arClass("w-10 h-10")}
                        />
                    </motion.button>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        key="panel"
                        initial={{ x: isLeft ? '-100%' : '100%' }}
                        animate={{ x: '0%' }}
                        exit={{ x: isLeft ? '-100%' : '100%' }}
                        transition={isResizing ? { duration: 0 } : { type: 'spring', stiffness: 380, damping: 38 }}
                        style={panelStyle}
                        className={arClass(
                            `fixed z-[9999] bottom-0 ${sideClass} ${isFull ? 'top-0 w-full h-full' : ''}
                            ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}
                            shadow-2xl flex flex-col border-l border-r
                            ${darkMode ? 'border-gray-800' : 'border-gray-200'}`
                        )}
                    >
                        {!isFull && (
                            <div
                                onMouseDown={beginResize('height')}
                                className={arClass(
                                    "absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize z-10 group"
                                )}
                                title="Drag to resize height"
                            >
                                <div className={arClass(
                                    `mx-auto mt-0.5 h-1 w-10 rounded-full transition-colors ${
                                        darkMode ? 'bg-gray-700 group-hover:bg-blue-500' : 'bg-gray-300 group-hover:bg-blue-500'
                                    }`
                                )} />
                            </div>
                        )}

                        {!isFull && (
                            <div
                                onMouseDown={beginResize('width')}
                                className={arClass(
                                    `absolute top-0 bottom-0 w-1.5 cursor-ew-resize z-10 group ${
                                        isLeft ? 'right-0' : 'left-0'
                                    }`
                                )}
                                title="Drag to resize width"
                            >
                                <div className={arClass(
                                    `mx-auto my-auto h-10 w-1 rounded-full transition-colors ${
                                        darkMode ? 'bg-gray-700 group-hover:bg-blue-500' : 'bg-gray-300 group-hover:bg-blue-500'
                                    }`
                                )} />
                            </div>
                        )}

                        <div className={arClass(
                            `flex items-center justify-between px-4 py-3.5 border-b flex-shrink-0 ${
                                darkMode ? 'border-gray-800' : 'border-gray-200'
                            }`
                        )}>
                            <div className={arClass("flex items-center gap-2")}>
                                <img
                                    src={darkMode ? logos.lightLogo : logos.darkLogo}
                                    alt={'Raqib AI Assistant'}
                                    title={user.name}
                                    className={arClass(
                                        `w-8 h-8 p-0.5 rounded-full object-cover flex-shrink-0  shadow-xl shadow-black/40 flex items-center justify-center
                             text-white ${darkMode ? 'bg-slate-100 hover:bg-slate-200' : 'bg-slate-200 hover:bg-slate-300'}`
                                    )}
                                />
                                <div className={arClass("flex flex-col leading-tight")}>
                                    <h1 className={arClass("text-base font-semibold")}>
                                        <Text>Raqib AI Assistant</Text>
                                    </h1>
                                    {user?.name && (
                                        <span className={arClass(
                                            `text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`
                                        )}>
                                            {user.name}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className={arClass("flex items-center gap-0.5")}>
                                <button
                                    onClick={clearChat}
                                    disabled={messages.length === 0}
                                    title={<Text>Clear chat</Text>}
                                    className={arClass(
                                        `w-8 h-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${iconBtnClass}`
                                    )}
                                >
                                    <Trash2 size={16} />
                                </button>

                                <button
                                    onClick={() => setPosition(isLeft ? 'right' : 'left')}
                                    title={isLeft ? <Text>Move to right</Text> : <Text>Move to left</Text>}
                                    className={arClass(
                                        `w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${iconBtnClass}`
                                    )}
                                >
                                    {isLeft ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                                </button>

                                <button
                                    onClick={() => setSize(isFull ? 'small' : 'full')}
                                    title={isFull ? <Text>Shrink panel</Text> : <Text>Expand panel</Text>}
                                    className={arClass(
                                        `w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${iconBtnClass}`
                                    )}
                                >
                                    {isFull ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                                </button>

                                <button
                                    onClick={() => setIsOpen(false)}
                                    title={<Text>Close</Text>}
                                    className={arClass(
                                        `w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${iconBtnClass}`
                                    )}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className={arClass(
                            `px-4 py-2 border-b flex-shrink-0 flex items-center justify-between ${
                                darkMode ? 'border-gray-800' : 'border-gray-200'
                            }`
                        )}>
                            <span className={arClass(
                                `text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-500'}`
                            )}>
                                <Text>Requests today</Text>
                            </span>
                            {!usageLoading && usage ? (
                                <span className={arClass(
                                    `text-xs font-semibold px-2 py-0.5 rounded-full ${
                                        usage.remainingRequests === 0
                                            ? darkMode ? 'bg-red-950 text-red-400' : 'bg-red-50 text-red-600'
                                            : usage.remainingRequests < 5
                                                ? darkMode ? 'bg-yellow-950 text-yellow-400' : 'bg-yellow-50 text-yellow-700'
                                                : darkMode ? 'bg-green-950 text-green-400' : 'bg-green-50 text-green-700'
                                    }`
                                )}>
                                    {usage.requests} / {usage.limit}
                                </span>
                            ) : (
                                <Loader2 size={12} className={arClass(
                                    `animate-spin ${darkMode ? 'text-gray-600' : 'text-gray-400'}`
                                )} />
                            )}
                        </div>

                        <div className={arClass(
                            `px-4 py-1.5 border-b flex-shrink-0 flex items-center gap-1.5 ${
                                darkMode ? 'border-gray-800 bg-gray-900/60' : 'border-gray-200 bg-gray-100/60'
                            }`
                        )}>
                            <Info size={12} className={arClass(
                                darkMode ? 'text-gray-500' : 'text-gray-400'
                            )} />
                            <span className={arClass(
                                `text-[11px] ${darkMode ? 'text-gray-500' : 'text-gray-500'}`
                            )}>
                                <Text>This chat isn't saved — it will be cleared when you reload the page.</Text>
                            </span>
                        </div>

                        <div className={arClass("flex-1 overflow-y-auto px-4 py-4 space-y-4")}>
                            {messages.length === 0 && !loading && (
                                <div className={arClass(
                                    `text-center py-12 rounded-xl border border-dashed ${
                                        darkMode ? 'border-gray-800 text-gray-500' : 'border-gray-200 text-gray-500'
                                    }`
                                )}>
                                    <MessageSquare size={28} className={arClass("mx-auto mb-3 opacity-50")} />
                                    <p className={arClass("text-sm font-medium")}>
                                        {firstName ? <Text>Hi {firstName}, no messages yet</Text> : <Text>No messages yet</Text>}
                                    </p>
                                    <p className={arClass("text-xs mt-1 opacity-75")}>
                                        <Text>Type a message below to get started</Text>
                                    </p>
                                </div>
                            )}

                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    className={arClass(
                                        `flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`
                                    )}
                                >
                                    <div className={arClass(
                                        `flex items-end gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`
                                    )}>
                                        {msg.role === 'user' ? (
                                            user?.profileImage ? (
                                                <img
                                                    src={user.profileImage}
                                                    alt={user.name || 'You'}
                                                    title={user.name}
                                                    className={arClass(
                                                        "w-6 h-6 rounded-full object-cover flex-shrink-0 shadow-black/50 shadow-sm"
                                                    )}
                                                />
                                            ) : (
                                                <div className={arClass(
                                                    `w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                        darkMode ? 'bg-gray-700' : 'bg-gray-200'
                                                    }`
                                                )}>
                                                    <User size={13} className={arClass(
                                                        darkMode ? 'text-gray-300' : 'text-gray-600'
                                                    )} />
                                                </div>
                                            )
                                        ) : (
                                            <img
                                    src={darkMode ? logos.lightLogo : logos.darkLogo}
                                    alt={'Raqib AI Assistant'}
                                    title={user.name}
                                    className={arClass(
                                        `w-6 h-6 p-0.5 rounded-full object-cover flex-shrink-0  shadow-xl shadow-black/60 flex items-center justify-center
                             text-white ${darkMode ? 'bg-slate-100 hover:bg-slate-200' : 'bg-slate-200 hover:bg-slate-300'}`
                                    )}
                                />
                                        )}

                                        <div>
                                            <div className={arClass(
                                                `px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                                                    msg.role === 'user'
                                                        ? 'bg-blue-600 text-white rounded-br-sm'
                                                        : darkMode
                                                            ? 'bg-gray-800 text-gray-100 rounded-bl-sm'
                                                            : 'bg-white border border-gray-100 text-gray-900 rounded-bl-sm shadow-sm'
                                                }`
                                            )}>
                                                {msg.content}
                                            </div>

                                          
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {loading && (
                                <div className={arClass("flex justify-start")}>
                                    <div className={arClass("flex items-end gap-2 max-w-[85%]")}>
                                        <div className={arClass(
                                            "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-blue-500 to-purple-600"
                                        )}>
                                           <img
                                    src={darkMode ? logos.lightLogo : logos.darkLogo}
                                    alt={'Raqib AI Assistant'}
                                    title={user.name}
                                    className={arClass(
                                        `w-6 h-6 p-0.5 rounded-full object-cover flex-shrink-0  shadow-xl shadow-black/60 flex items-center justify-center
                             text-white ${darkMode ? 'bg-slate-100 hover:bg-slate-200' : 'bg-slate-200 hover:bg-slate-300'}`
                                    )}
                                />
                                        </div>
                                        <div className={arClass(
                                            `px-3.5 py-2.5 rounded-2xl rounded-bl-sm flex items-center gap-1.5 ${
                                                darkMode ? 'bg-gray-800' : 'bg-white border border-gray-100 shadow-sm'
                                            }`
                                        )}>
                                            <Loader2 size={14} className={arClass("animate-spin text-blue-500")} />
                                            <span className={arClass(
                                                `text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`
                                            )}>
                                                <Text>Thinking...</Text>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div ref={scrollEndRef} />
                        </div>

                        <div className={arClass(
                            `flex-shrink-0 border-t ${darkMode ? 'border-gray-800' : 'border-gray-200'}`
                        )}>
                            {errorMsg && (
                                <div className={arClass(
                                    `mx-4 mt-3 flex items-start gap-2 text-sm px-3 py-2 rounded-lg ${
                                        darkMode ? 'bg-red-950/40 text-red-300 border border-red-900' : 'bg-red-50 text-red-700 border border-red-200'
                                    }`
                                )}>
                                    <AlertTriangle size={15} className={arClass("mt-0.5 flex-shrink-0")} />
                                    <div>
                                        <p className={arClass("text-sm leading-snug")}>{errorMsg}</p>
                                        {sensitiveTypes.length > 0 && (
                                            <p className={arClass(
                                                `mt-1 text-xs ${darkMode ? 'text-red-400' : 'text-red-500'}`
                                            )}>
                                                <Text>Detected:</Text> {sensitiveTypes.map((t) => (
                                                    <Text key={t}>{SENSITIVE_TYPE_LABELS_KEYS[t] || t}</Text>
                                                )).join(', ')}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className={arClass("p-3")}>
                                <div className={arClass("relative")}>
                                    <input
                                        type="text"
                                        id="prompt"
                                        name="prompt"
                                        value={prompt}
                                        onChange={handleChange}
                                        placeholder={
                                            usage?.remainingRequests === 0
                                                ? textTranslator("Daily limit reached — try again tomorrow","AI")
                                                : textTranslator("Ask me anything...","AI")
                                        }
                                        disabled={usage?.remainingRequests === 0}
                                        className={arClass(
                                            `w-full px-4 py-3 pr-12 rounded-xl border text-sm focus:ring-2 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                                errorMsg
                                                    ? 'focus:ring-red-500 border-red-400'
                                                    : 'focus:ring-blue-500 focus:border-transparent'
                                            } ${
                                                darkMode
                                                    ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                                                    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                                            }`
                                        )}
                                    />
                                    <button
                                        type="submit"
                                        disabled={!canSubmit}
                                        aria-label={<Text>Send prompt</Text>}
                                        className={arClass(
                                            `absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                                canSubmit
                                                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                                                    : darkMode
                                                        ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                            }`
                                        )}
                                    >
                                        <Send size={14} />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}